'use client';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import { useShallow } from 'zustand/shallow';
import { usePageAction } from '@repo/hooks/usePageAction';
import { useHYSACalculatorStore } from '../CalculatorForm/calculatorStore';
import { formatCurrency } from '../CalculatorForm/utils';
import { useSummaryBreakdownStore } from '../stores/summaryBreakdownStore';

export function Results() {
  const fireHysaCalculatorInteractedPageAction = usePageAction({
    actionName: 'hysa_calculator_collapsable',
    actionSection: 'banking',
    actionType: 'select',
  });
  const { initialInvestment, interestEarned, total, totalInvested, totalMonthlyContribution } =
    useHYSACalculatorStore(
      useShallow(state => ({
        initialInvestment: formatCurrency({
          value: state.results.initialDeposit,
        }),
        interestEarned: formatCurrency({
          value: state.results.interestEarned,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        total: formatCurrency({
          value: state.results.total,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        totalInvested: formatCurrency({
          value: state.results.totalInvested,
        }),
        totalMonthlyContribution: formatCurrency({
          value: state.results.totalMonthlyContributions,
        }),
      })),
    );

  const { isBreakdownOpen, handleBreakdownOpen } = useSummaryBreakdownStore(
    useShallow(state => ({
      handleBreakdownOpen: state.handleBreakdownOpen,
      isBreakdownOpen: state.isBreakdownOpen,
    })),
  );

  return (
    <Box
      sx={{
        backgroundColor: 'greens.extraLight',
        borderRadius: '0.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '.5rem',
        paddingX: '1rem',
        paddingY: '1rem',
      }}
    >
      <Typography id="total-interest-earned-label" variant="textBold">
        Total interest earned
      </Typography>
      <Typography
        aria-labelledby="total-interest-earned-label"
        variant="text"
        sx={{
          color: 'greens.main',
          fontSize: { xs: '2rem', sm: '1.5rem', lg: '2rem' },
          fontWeight: 500,
        }}
      >
        {interestEarned}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '.125rem',
          paddingTop: {
            xs: '0.5rem',
          },
        }}
      >
        <Typography
          id="future-value-label"
          variant="textSmall"
          sx={{
            color: '#3D3D3B',
            fontWeight: 325,
            lineHeight: '154%',
            letterSpacing: '0.065px',
          }}
        >
          Future value:{' '}
        </Typography>
        <Typography
          aria-labelledby="future-value-label"
          variant="textSmallBold"
          sx={{ color: 'common.black', lineHeight: '20px', letterSpacing: '0.065px' }}
        >
          {total}
        </Typography>
      </Box>
      <Accordion
        disableGutters
        elevation={0}
        expanded={isBreakdownOpen}
        onChange={() => {
          fireHysaCalculatorInteractedPageAction();
          handleBreakdownOpen(!isBreakdownOpen);
        }}
        slotProps={{
          root: {
            sx: {
              backgroundColor: 'greens.extraLight',
              border: 'none',
              boxShadow: 'none',
              '&:last-of-type': {
                borderBottom: 'none',
              },
              paddingTop: {
                xs: '0.5rem',
              },
              paddingBottom: '1rem',
              position: 'relative',
              '&.Mui-expanded': {
                backgroundColor: 'greens.extraLight',
              },
              '&.MuiPaper-root': {
                backgroundColor: 'greens.extraLight',
                paddingY: 0,
              },
            },
          },
          heading: {
            component: 'span',
          },
        }}
      >
        <AccordionSummary
          expandIcon={
            <KeyboardArrowDownIcon
              sx={{
                color: 'common.black',
                fontSize: '.8125rem',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '20px',
                letterSpacing: '0.065px',
                width: '1.5rem',
                height: '1.5rem',
              }}
            />
          }
          slotProps={{
            content: {
              sx: {
                color: 'common.black',
                fontFamily: 'var(--font-gotham), Gotham Display, sans-serif',
                fontSize: '.8125rem',
                fontWeight: 500,
                letterSpacing: '0.065px',
                lineHeight: '20px',
                margin: 0,
              },
            },
            expandIconWrapper: {
              sx: {
                position: 'absolute',
                right: '0.5rem',
              },
            },
            root: {
              sx: {
                backgroundColor: 'greens.extraLight',
                borderRadius: '.5rem',
                minHeight: 0,
                paddingRight: {
                  xs: '1rem',
                  sm: '0',
                },
                paddingLeft: '0rem',
                '&.Mui-expanded': {
                  backgroundColor: 'greens.extraLight',
                },
                '&:focus': {
                  borderColor: 'greens.dark',
                },
                '&:hover': {
                  backgroundColor: 'greens.extraLight',
                  outlineColor: 'transparent',
                },
                '&.Mui-focusVisible': {
                  backgroundColor: 'transparent',
                  outlineColor: 'blues.light',
                },
              },
            },
          }}
        >
          View breakdown
        </AccordionSummary>
        <AccordionDetails
          aria-label="High yield savings account breakdown"
          sx={{
            paddingX: 0,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Box
              sx={{
                alignItems: 'flex-start',
                borderBottomColor: 'greens.lighter',
                borderBottomStyle: 'solid',
                borderBottomWidth: '1px',
                display: 'flex',
                gap: '.125rem',
                paddingBottom: '.5rem',
              }}
            >
              <Typography
                id="initial-deposit-label"
                variant="textSmall"
                sx={{ flex: '1 0 0', lineHeight: '154%', color: '#3D3D3B' }}
              >
                Initial deposit
              </Typography>
              <Typography
                aria-labelledby="initial-deposit-label"
                variant="textSmallBold"
                sx={{ color: '#3D3D3B', letterSpacing: '0.065px', lineHeight: '20px' }}
              >
                {initialInvestment}
              </Typography>
            </Box>
            <Box
              sx={{
                alignItems: 'flex-start',
                borderBottomColor: 'greens.lighter',
                borderBottomStyle: 'solid',
                borderBottomWidth: '1px',
                display: 'flex',
                gap: '.125rem',
                paddingBottom: '.5rem',
              }}
            >
              <Typography
                id="monthly-deposits-label"
                variant="textSmall"
                sx={{ color: '#3D3D3B', flex: '1 0 0', lineHeight: '154%' }}
              >
                Monthly deposits
              </Typography>
              <Typography
                aria-labelledby="monthly-deposits-label"
                variant="textSmallBold"
                sx={{ color: '#3D3D3B', letterSpacing: '0.065px', lineHeight: '20px' }}
              >
                {totalMonthlyContribution}
              </Typography>
            </Box>
            <Box
              sx={{
                alignItems: 'flex-start',
                borderBottomColor: 'greens.lighter',
                borderBottomStyle: 'solid',
                borderBottomWidth: '1px',
                display: 'flex',
                gap: '.125rem',
                paddingBottom: '.5rem',
              }}
            >
              <Typography
                id="total-deposits-label"
                variant="textSmall"
                sx={{ color: '#3D3D3B', flex: '1 0 0', lineHeight: '154%' }}
              >
                Total deposits
              </Typography>
              <Typography
                aria-labelledby="total-deposits-label"
                variant="textSmallBold"
                sx={{ color: '#3D3D3B', letterSpacing: '0.065px', lineHeight: '20px' }}
              >
                {totalInvested}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: '.125rem',
                alignItems: 'flex-start',
                paddingBottom: '.5rem',
                borderBottomWidth: '1px',
                borderBottomStyle: 'solid',
                borderBottomColor: 'greens.lighter',
              }}
            >
              <Typography
                id="interest-earned-label"
                variant="textSmall"
                sx={{ color: '#3D3D3B', flex: '1 0 0', fontWeight: 325, lineHeight: '154%' }}
              >
                Interest earned
              </Typography>
              <Typography
                aria-labelledby="interest-earned-label"
                variant="textSmallBold"
                sx={{ color: 'greens.main', letterSpacing: '0.065px', lineHeight: '20px' }}
              >
                {interestEarned}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: '.125rem',
                alignItems: 'flex-start',
                paddingBottom: '.5rem',
              }}
            >
              <Typography
                id="total-label"
                sx={{
                  color: 'common.black',
                  flex: '1 0 0',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  lineHeight: '20px',
                  letterSpacing: '0.065px',
                }}
              >
                Total
              </Typography>
              <Typography
                aria-labelledby="total-label"
                sx={{
                  color: 'common.black',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  lineHeight: '20px',
                  letterSpacing: '0.065px',
                }}
              >
                {total}
              </Typography>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
